import { App, Form } from "antd";
import { RcFile } from "antd/es/upload";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ContractPayload,
    getApiError,
    getCatalogFinGroupsThunk,
    PostContractCreateParams,
    postContractCreateThunk,
    PutContractUpdateParams,
    putContractUpdateThunk,
} from "tm-shared/api";
import { Contract } from "tm-shared/types";
import { isPointChains, SerializedFile, serializeFile } from "tm-shared/utils";

import { getPointStorageId, usePoint } from "@/domains/point";
import { useAsyncAction } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import BaseButton from "@/shared/ui/BaseButton";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import {
    patchContractFormValues,
    setContractFormValues,
} from "@/store/slices/work";
import { ContractFormValues } from "@/types/form";

import { ContractFormFields } from "./components/ContractFormFields";
import { ContractTypeFields } from "./components/ContractTypeFields";
import { FileUploadSection } from "./components/FileUploadSection";

const EMPTY_VALUES: Partial<ContractFormValues> = {
    paybackPeriod: undefined,
    paymentTerms: undefined,
    placePrice: undefined,
    materialsPrice: undefined,
    deferredDelivery: undefined,
    deferredTermForm1: undefined,
    deferredTermForm2: undefined,
    costInCash: undefined,
    isAllFinGroups: false,
    finGroups: [],
    discountInPercentage: undefined,
    products: [],
};

interface AddContractProps {
    editMode?: boolean;
    contract?: Contract;
    fop?: string;
}

const AddContract: React.FC<AddContractProps> = ({
    editMode = false,
    contract,
    fop,
}) => {
    const { pointId = "" } = useParams();
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const finGroupOptions = useAppSelector((s) => s.work.options.finGroups);
    const contractFormValues = useAppSelector(
        (state) => state.work.contractFormValues,
    );

    const { point, pointLoading } = usePoint(pointId);

    const [form] = Form.useForm<ContractFormValues>();
    const [oldFiles, setOldFiles] = useState<string[]>(contract?.files ?? []);

    const initialValues = useMemo<Partial<ContractFormValues>>(() => {
        if (editMode && contract) {
            const {
                files: _files,
                targetId: _pointId,
                id: _id,
                context,
                ...rest
            } = contract;
            return {
                ...rest,
                ...context,
            };
        }
        if (contractFormValues) {
            return contractFormValues;
        }
        return {};
    }, [editMode, contract, contractFormValues]);

    const handleDeleteFile = (file: string) => {
        setOldFiles((prev) => prev.filter((item) => item !== file));
    };

    const handleValuesChange = (changedValues: Partial<ContractFormValues>) => {
        const { files: _, ...values } = changedValues;

        if ("type" in changedValues) {
            form.setFieldsValue(EMPTY_VALUES);
        }

        dispatch(patchContractFormValues(values));
    };

    const [handleCreateContract, loading] = useAsyncAction(
        async (values: ContractFormValues) => {
            const {
                name,
                type,
                description,
                startDate,
                endDate,
                files,
                targetType,
                ...restValues
            } = values;

            const rcFiles = files
                ?.map((f) => {
                    return f.originFileObj as RcFile | undefined;
                })
                .filter((f): f is RcFile => !!f);

            let transformedFiles: SerializedFile[] | undefined;

            if (rcFiles?.length) {
                try {
                    transformedFiles = await Promise.all(
                        rcFiles.map((file) => serializeFile(file)),
                    );
                } catch (e) {
                    console.error("Помилка перетворення файлів:", e);
                }
            }

            const targetTypeVocabulary = {
                point: point?.id || "",
                network:
                    point && isPointChains(point)
                        ? point.typeContext.networkId
                        : "",
                contragent: point?.market?.contragent?.id ?? "",
                storage: (point && getPointStorageId(point)) ?? "",
                area: point?.areaId ?? "",
            };

            const request: ContractPayload = {
                name,
                type,
                targetType,
                targetId: targetTypeVocabulary[targetType] ?? point?.id ?? "",
                description,
                startDate,
                endDate,
                files: transformedFiles,
                context: {
                    ...restValues,
                },
            };

            try {
                if (editMode && contract) {
                    const filesToRemove =
                        contract?.files?.filter(
                            (file) => !oldFiles.includes(file),
                        ) ?? [];

                    const editRequest: PutContractUpdateParams = {
                        ...request,
                        contractId: contract.id,
                        ...(filesToRemove.length && {
                            deleteFiles: filesToRemove,
                        }),
                    };

                    await dispatch(
                        putContractUpdateThunk(editRequest),
                    ).unwrap();

                    notification.success({ title: "Контракт оновлено" });
                } else {
                    const createRequest: PostContractCreateParams = {
                        ...request,
                    };

                    await dispatch(
                        postContractCreateThunk(createRequest),
                    ).unwrap();

                    notification.success({ title: "Контракт створений" });
                }

                dispatch(setContractFormValues(null));
                navigate(-1);
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    title: "Помилка",
                    description: displayMessage,
                });

                console.error(error);
            }
        },
    );

    useEffect(() => {
        if (!finGroupOptions || finGroupOptions?.length === 0) {
            dispatch(getCatalogFinGroupsThunk());
        }
    }, [dispatch, finGroupOptions]);

    return (
        <>
            {!editMode && (
                <Header title="Додати контракт" back titleFontSize={16} />
            )}

            <MainContainer>
                <div style={{ marginBottom: 16 }}>
                    {!editMode ? (
                        <SecondaryText fontSize={16}>
                            Внесіть необхідні дані для додавання контракту до
                            ТТ:
                        </SecondaryText>
                    ) : (
                        <PrimaryText fontSize={16}>{fop}</PrimaryText>
                    )}
                </div>

                <Form
                    form={form}
                    name="addContract"
                    style={{ width: "100%" }}
                    autoComplete="off"
                    requiredMark={false}
                    initialValues={initialValues}
                    onValuesChange={handleValuesChange}
                    layout="vertical"
                    onFinish={handleCreateContract}
                >
                    <ContractFormFields
                        editMode={editMode}
                        point={point}
                        pointLoading={pointLoading}
                    />

                    <ContractTypeFields
                        point={point}
                        pointLoading={pointLoading}
                    />

                    <FileUploadSection
                        oldFiles={oldFiles}
                        onDeleteFile={handleDeleteFile}
                    />

                    <BaseButton
                        type="primary"
                        loading={loading}
                        disabled={loading}
                        htmlType="submit"
                    >
                        {editMode ? "Оновити контракт" : "Додати контракт"}
                    </BaseButton>
                </Form>
            </MainContainer>
        </>
    );
};

export default AddContract;
